import { defineStore } from 'pinia';

import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { useOrderStore } from '@/Stores/Web/orderStore';

import { apiClient } from '@/Clients/ApiClient';
import CheckoutService from '@/Services/CheckoutService';

const checkoutService = new CheckoutService(apiClient);

export const useInstallmentsStore = defineStore('installments', {
    state: () => ({
        installments: [],
        currencySymbol: null,
        installmentValue: null,
        isChargingInstallments: false,
        actionType: null,
        newPlanAmountConverted: null,
        previousPlanAmountConverted: null,
        upgradeAmountConverted: null,
    }),
    actions: {
        async getInstallmentsData(projectId, exchangeRate, orderCode = null) {
            const { appliedGiftCardIDs } = useOrderStore();

            if (orderCode) {
                return await checkoutService.calculateOrderInstallmentPlan(projectId, exchangeRate, orderCode, appliedGiftCardIDs);
            }

            return await checkoutService.calculateCartInstallmentPlan(projectId, exchangeRate, appliedGiftCardIDs);
        },
        setInstallments({ data }) {
            this.installments = data.installments;
            this.currencySymbol = data.currencySymbol;
            this.actionType = data.actionType;
            this.newPlanAmountConverted = data.newPlanAmountConverted;
            this.previousPlanAmountConverted = data.previousPlanAmountConverted;
            this.upgradeAmountConverted = data.upgradeAmountConverted;
            // We take the last installment because the first installment may be larger if it cannot be divided evenly.
            this.installmentValue = data.installments[data.installments.length - 1].amountConverted;
        },
        async handleInstallmentsDataFlow(orderCode = null) {
            const { projectID } = useProjectContextStore();
            const { conversionRate } = useCurrencyStore();

            const { data } = await this.getInstallmentsData(projectID, conversionRate, orderCode);
            this.setInstallments(data);
        },
        setActionStatus(status) {
            this.isChargingInstallments = status;
        },
    },
    getters: {
        installmentsCount() {
            return this.installments?.length;
        },
        firstInstallmentAmount() {
            return this.installments[0]?.amountConverted;
        },
        canBePaidInInstallments() {
            const { installmentsSettings } = useProjectContextStore();
            const { currentOrderDetails } = useOrderStore();

            const isEligibleForInstallments =  currentOrderDetails?.isEligibleForInstallments;
            const effectivePayableAmount  = currentOrderDetails?.cart.effectiveOutstandingAmountAfterCreditDiscount;

            return isEligibleForInstallments && effectivePayableAmount >= installmentsSettings?.installmentsMinPaymentAmount;
        },
    },
});
